// Mock data service for testing certificate functionality

// Mock property data
export const mockProperty = {
  id: 2,
  property_id: "PROP-002",
  name: "Test Property",
  address: "123 Test Street, London, SW1A 1AA",
  status: "active",
  client: {
    id: 1,
    name: "Test Client Ltd"
  },
  created_at: "2024-01-15T10:30:00Z",
  updated_at: "2024-01-15T10:30:00Z"
};

// Mock certificate types based on Laravel config
export const mockCertificateTypes = {
  6: "Gas Safe Certificate",
  7: "NIC Certificate", 
  8: "Asbestos Register",
  9: "Fire Risk Assessment",
  10: "Legionella",
  30: "Fire Alarm",
  31: "Emergency Lighting",
  32: "Fire Extinguisher Certificate",
  33: "PAT Testing",
  29: "240V Certificate",
};

// Mock existing certificates
export const mockCertificates = [
  {
    id: 1,
    name: "Gas Safe Certificate 2024",
    document_type_id: 6,
    type: "Gas Safe Certificate",
    date_completed: "2024-01-15",
    date_expiration: "2025-01-15",
    date_contractor_appointment: "2024-01-10",
    visible_to_client: true,
    status: "active",
    file_url: "/documents/gas-safe-2024.pdf",
    created_at: "2024-01-15T10:30:00Z"
  },
  {
    id: 2,
    name: "Fire Risk Assessment 2024",
    document_type_id: 9,
    type: "Fire Risk Assessment",
    date_completed: "2024-02-01",
    date_expiration: "2025-02-01",
    date_contractor_appointment: "2024-01-28",
    visible_to_client: true,
    status: "active",
    file_url: "/documents/fire-risk-2024.pdf",
    created_at: "2024-02-01T14:20:00Z"
  },
  {
    id: 3,
    name: "PAT Testing Certificate",
    document_type_id: 33,
    type: "PAT Testing",
    date_completed: "2024-03-10",
    date_expiration: "2025-03-10",
    date_contractor_appointment: "2024-03-05",
    visible_to_client: false,
    status: "active",
    file_url: "/documents/pat-testing-2024.pdf",
    created_at: "2024-03-10T09:15:00Z"
  }
];

// Mock API responses
export const mockApiResponses = {
  // Get property
  getProperty: {
    success: true,
    data: mockProperty
  },

  // Get certificate types
  getCertificateTypes: {
    success: true,
    data: mockCertificateTypes
  },

  // Store certificate success
  storeCertificateSuccess: {
    success: true,
    data: {
      id: 4,
      name: "New Certificate",
      document_type_id: 6,
      type: "Gas Safe Certificate",
      date_completed: "2024-12-01",
      date_expiration: "2025-12-01",
      date_contractor_appointment: "2024-11-25",
      visible_to_client: true,
      status: "active",
      file_url: "/documents/new-certificate.pdf",
      created_at: "2024-12-01T10:00:00Z"
    },
    message: "Certificate added successfully"
  },

  // Store certificate error
  storeCertificateError: {
    success: false,
    message: "Error adding certificate: File is required"
  },

  // Validation error
  validationError: {
    success: false,
    message: "Validation failed",
    errors: {
      name: ["The name field is required."],
      document_type_id: ["The document type field is required."],
      document: ["The document field is required."]
    }
  }
};

// Mock service functions
export const mockPropertiesService = {
  getProperty: async (id) => {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 500));
    
    if (id === "2") {
      return mockApiResponses.getProperty;
    } else {
      return {
        success: false,
        message: "Property not found"
      };
    }
  },

  storeCertificate: async (propertyId, formData) => {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Check if required fields are present
    const name = formData.get("name");
    const documentTypeId = formData.get("document_type_id");
    const document = formData.get("document");
    
    if (!name || !documentTypeId || !document) {
      return mockApiResponses.validationError;
    }
    
    // Simulate success
    return mockApiResponses.storeCertificateSuccess;
  }
};

// Mock file validation
export const mockFileValidation = {
  isValid: true,
  error: null
};

// Mock document validation utility
export const mockDocumentValidation = {
  validateDocumentFile: (file) => {
    if (!file) {
      return { isValid: false, error: "No file selected" };
    }
    
    const allowedTypes = [
      'application/pdf',
      'image/jpeg',
      'image/jpg',
      'image/png',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];
    
    if (!allowedTypes.includes(file.type)) {
      return { isValid: false, error: "Invalid file type. Please select PDF, JPG, PNG, or DOC files." };
    }
    
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      return { isValid: false, error: "File size too large. Maximum size is 10MB." };
    }
    
    return { isValid: true, error: null };
  },
  
  getDocumentAcceptValue: () => {
    return ".pdf,.jpg,.jpeg,.png,.doc,.docx";
  }
};

// Mock date utilities
export const mockDateUtils = {
  formatDateUK: (date) => {
    if (!date) return '';
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  },
  
  addYearsToDate: (date, years) => {
    const d = new Date(date);
    d.setFullYear(d.getFullYear() + years);
    return mockDateUtils.formatDateUK(d);
  },
  
  isValidDateUK: (dateString) => {
    if (!dateString) return false;
    const parts = dateString.split('/');
    if (parts.length !== 3) return false;
    
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const year = parseInt(parts[2], 10);
    
    if (isNaN(day) || isNaN(month) || isNaN(year)) return false;
    if (day < 1 || day > 31) return false;
    if (month < 1 || month > 12) return false;
    if (year < 1900 || year > 2100) return false;
    
    return true;
  },
  
  toISODate: (dateString) => {
    if (!dateString) return '';
    const parts = dateString.split('/');
    if (parts.length !== 3) return '';
    
    const day = parts[0];
    const month = parts[1];
    const year = parts[2];
    
    return `${year}-${month}-${day}`;
  }
};

// Export all mock data
export default {
  mockProperty,
  mockCertificateTypes,
  mockCertificates,
  mockApiResponses,
  mockPropertiesService,
  mockFileValidation,
  mockDocumentValidation,
  mockDateUtils
};
